import type { IVector3 } from '@seddi/math-v2';

import { type PerspectiveCamera, Vector3 } from '@seddi/three';

import { OrbitControls } from '../../viewer/orbitControls';

const approxEquals = (a: Vector3, b: Vector3, epsilon = Number.EPSILON) => {
  return (
    Math.abs(a.x - b.x) < epsilon && Math.abs(a.y - b.y) < epsilon && Math.abs(a.z - b.z) < epsilon
  );
};

type ConstructorArgs = {
  camera: PerspectiveCamera;
  canvas: HTMLCanvasElement;
};

export class AppViewerTool {
  readonly camera: PerspectiveCamera;
  readonly canvas: HTMLCanvasElement;

  dispose: () => void;

  private readonly orbitControls: OrbitControls;
  private readonly zoomDirection = new Vector3();

  private readonly cameraPositionPrev = new Vector3();

  constructor({ camera, canvas }: ConstructorArgs) {
    this.camera = camera;
    this.canvas = canvas;
    // init orbit controls
    this.orbitControls = new OrbitControls(camera, canvas);
    this.orbitControls.dampingFactor = 0.1;
    this.orbitControls.enableDamping = true;
    // init default camera
    this.lookAt({ x: 0.0, y: 1.2, z: 3.0 }, { x: 0.0, y: 1.2, z: 0.0 });

    this.dispose = function () {
      this.orbitControls.dispose();
    };
  }

  lookAt(origin: IVector3, target: IVector3) {
    this.camera.position.copy(origin);
    this.camera.lookAt(target.x, target.y, target.z);
    this.orbitControls.target.copy(target);
  }

  setMaxDistance(distance: number) {
    this.orbitControls.maxDistance = distance;
  }

  zoomIn() {
    this.adjustZoom('in');
  }

  zoomOut() {
    this.adjustZoom('out');
  }

  private adjustZoom(direction: 'in' | 'out') {
    const target = this.orbitControls.target;

    this.zoomDirection.copy(this.camera.position).sub(target);

    const currentDistance = this.zoomDirection.length();
    if (currentDistance === 0) {
      return;
    }

    const zoomScale = Math.pow(0.95, this.orbitControls.zoomSpeed);
    const scaleFactor = direction === 'in' ? zoomScale : 1 / zoomScale;

    let newDistance = currentDistance * scaleFactor;
    newDistance = Math.max(this.orbitControls.minDistance, Math.min(this.orbitControls.maxDistance, newDistance));

    this.zoomDirection.setLength(newDistance);
    this.camera.position.copy(target).add(this.zoomDirection);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onAnimationTick(): boolean {
    this.orbitControls.update();

    const needsDraw = !approxEquals(this.cameraPositionPrev, this.camera.position, 1e-4);

    this.cameraPositionPrev.copy(this.camera.position);

    return needsDraw;
  }

  onUpdate() {
    // Do nothing.
  }
}
